import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import { formatCurrency, formatPercent, getReturnColor } from '@/lib/utils'
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ZAxis, Legend, ReferenceLine,
} from 'recharts'
import HeatmapTable from '@/components/charts/HeatmapTable'

type Tab = 'overview' | 'univariate' | 'weights' | 'correlations' | 'marginal' | 'skewsharpe' | 'uniqueness' | 'corrquadrant' | 'conditional'

const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'univariate', label: 'Univariate' },
  { key: 'weights', label: 'Model Weights' },
  { key: 'correlations', label: 'Correlations' },
  { key: 'marginal', label: 'Marginal Impact' },
  { key: 'skewsharpe', label: 'Skew-Sharpe' },
  { key: 'uniqueness', label: 'Uniqueness' },
  { key: 'corrquadrant', label: 'Corr Quadrant' },
  { key: 'conditional', label: 'Conditional' },
]

const pct = (v: number | null | undefined) => v == null ? '-' : `${(v * 100).toFixed(2)}%`
const dec = (v: number | null | undefined, d = 2) => v == null ? '-' : v.toFixed(d)
const fmtImpact = (v: number | null | undefined) => {
  if (v == null) return '-'
  const s = (v * 100).toFixed(2)
  return v > 0 ? `+${s}%` : `${s}%`
}

export default function PortfolioAnalysis() {
  const [tab, setTab] = useState<Tab>('overview')
  const [corrType, setCorrType] = useState('full')
  const [condBench, setCondBench] = useState('SPTR')
  const [condMetric, setCondMetric] = useState('performance')
  const [months, setMonths] = useState(36)

  // ── Queries ──
  const { data: portfolio, isLoading } = useQuery({
    queryKey: ['portfolio'],
    queryFn: () => api.get('/portfolio').then(r => r.data),
  })

  const { data: univariate, isLoading: uniLoading } = useQuery({
    queryKey: ['univariate', months],
    queryFn: () => api.get(`/portfolio/univariate?months=${months}`).then(r => r.data),
    enabled: tab === 'univariate' || tab === 'overview',
  })

  const { data: correlations, isLoading: corrLoading } = useQuery({
    queryKey: ['correlations', corrType, months],
    queryFn: () => api.get(`/portfolio/correlations?type=${corrType}&months=${months}`).then(r => r.data),
    enabled: tab === 'correlations',
  })

  const { data: weights, isLoading: weightsLoading } = useQuery({
    queryKey: ['weights'],
    queryFn: () => api.get('/portfolio/weights').then(r => r.data),
    enabled: tab === 'weights',
  })

  const { data: marginal, isLoading: marginalLoading } = useQuery({
    queryKey: ['marginal'],
    queryFn: () => api.get('/portfolio/marginal').then(r => r.data),
    enabled: tab === 'marginal',
  })

  const { data: skewSharpe, isLoading: ssLoading } = useQuery({
    queryKey: ['skew-sharpe', months],
    queryFn: () => api.get(`/portfolio/skew-sharpe?months=${months}`).then(r => r.data),
    enabled: tab === 'skewsharpe',
  })

  const { data: conditional, isLoading: condLoading } = useQuery({
    queryKey: ['conditional'],
    queryFn: () => api.get('/portfolio/conditional').then(r => r.data),
    enabled: tab === 'conditional',
  })

  const { data: uniqueness, isLoading: uniqLoading } = useQuery({
    queryKey: ['uniqueness', months],
    queryFn: () => api.get(`/portfolio/uniqueness?months=${months}`).then(r => r.data),
    enabled: tab === 'uniqueness',
  })

  const { data: corrQuadrant, isLoading: cqLoading } = useQuery({
    queryKey: ['correlation-quadrant', months],
    queryFn: () => api.get(`/portfolio/correlation-quadrant?months=${months}`).then(r => r.data),
    enabled: tab === 'corrquadrant',
  })

  // ── Filtered conditional data ──
  const filteredCond = useMemo(() => {
    if (!conditional) return []
    return conditional.filter((r: any) => r.benchmark === condBench && r.metric === condMetric)
  }, [conditional, condBench, condMetric])

  const condBenchmarks = useMemo(() => {
    if (!conditional) return []
    return [...new Set(conditional.map((r: any) => r.benchmark))] as string[]
  }, [conditional])

  const condMetrics = useMemo(() => {
    if (!conditional) return []
    return [...new Set(conditional.map((r: any) => r.metric))] as string[]
  }, [conditional])

  // ── Scatter data ──
  const scatterData = useMemo(() => {
    if (!skewSharpe?.points) return { funds: [], portfolio: [] }
    const funds = skewSharpe.points.filter((p: any) => p.category === 'fund')
    const port = skewSharpe.points.filter((p: any) => p.category === 'portfolio')
    return { funds, portfolio: port }
  }, [skewSharpe])

  if (isLoading) {
    return <div className="flex justify-center p-8"><span className="spinner" /></div>
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Portfolio Analysis</h1>
        <p className="text-muted-foreground">Current portfolio holdings, metrics, and analytics</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="text-sm text-muted-foreground">Total Value</div>
          <div className="text-xl font-bold">{formatCurrency(portfolio?.total_value)}</div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="text-sm text-muted-foreground">Holdings</div>
          <div className="text-xl font-bold">{portfolio?.holdings?.length || 0}</div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="text-sm text-muted-foreground">As of</div>
          <div className="text-xl font-bold">{portfolio?.value_date || '-'}</div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              tab === t.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {tab === 'overview' && (
        <div className="space-y-6">
          {/* Holdings table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-4 border-b border-border"><h2 className="font-semibold">Current Portfolio</h2></div>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Fund</th>
                    <th className="text-right">Value</th>
                    <th className="text-right">Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {portfolio?.holdings?.map((h: any) => (
                    <tr key={h.fund_id}>
                      <td className="font-medium">{h.fund_name}</td>
                      <td className="text-right font-mono">{formatCurrency(h.value)}</td>
                      <td className="text-right font-mono">{formatPercent(h.weight)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Quick univariate */}
          {univariate && univariate.length > 0 && (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="p-4 border-b border-border"><h2 className="font-semibold">Univariate Summary</h2></div>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Fund</th>
                      <th className="text-right">Return</th>
                      <th className="text-right">Vol</th>
                      <th className="text-right">Sharpe</th>
                      <th className="text-right">Sortino</th>
                      <th className="text-right">Max DD</th>
                      <th className="text-right">Cur DD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {univariate.map((u: any) => (
                      <tr key={u.name}>
                        <td className="font-medium">{u.name}</td>
                        <td className={`text-right font-mono ${getReturnColor(u.ann_return)}`}>{pct(u.ann_return)}</td>
                        <td className="text-right font-mono">{pct(u.ann_volatility)}</td>
                        <td className="text-right font-mono">{dec(u.sharpe_ratio)}</td>
                        <td className="text-right font-mono">{dec(u.sortino_ratio)}</td>
                        <td className="text-right font-mono text-red-600">{pct(u.max_drawdown)}</td>
                        <td className="text-right font-mono text-red-600">{pct(u.current_drawdown)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── UNIVARIATE TAB ── */}
      {tab === 'univariate' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <label className="text-sm text-muted-foreground">Lookback:</label>
            <select
              value={months}
              onChange={e => setMonths(Number(e.target.value))}
              className="h-8 px-2 border border-input rounded-md bg-background text-sm"
            >
              {[12, 24, 36, 48, 60, 84, 120].map(m => (
                <option key={m} value={m}>{m} months</option>
              ))}
            </select>
          </div>
          {uniLoading ? (
            <div className="flex justify-center p-8"><span className="spinner" /></div>
          ) : univariate && univariate.length > 0 ? (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="p-4 border-b border-border"><h2 className="font-semibold">Full Univariate Analysis</h2></div>
              <div className="overflow-x-auto">
                <table className="data-table text-xs">
                  <thead>
                    <tr>
                      <th className="sticky left-0 bg-card z-10">Fund</th>
                      <th className="text-right">Return</th>
                      <th className="text-right">Vol</th>
                      <th className="text-right">Sharpe</th>
                      <th className="text-right">Sortino</th>
                      <th className="text-right">Max DD</th>
                      <th className="text-right">Cur DD</th>
                      <th className="text-right">Recovery</th>
                      <th className="text-right">Skew</th>
                      <th className="text-right">Kurt</th>
                      <th className="text-right">VaR 95</th>
                      <th className="text-right">CVaR 95</th>
                      <th className="text-right">Win%</th>
                      <th className="text-right">Best Mo</th>
                      <th className="text-right">Worst Mo</th>
                      <th className="text-right">Months</th>
                    </tr>
                  </thead>
                  <tbody>
                    {univariate.map((u: any) => (
                      <tr key={u.name}>
                        <td className="font-medium sticky left-0 bg-card z-10 whitespace-nowrap">{u.name}</td>
                        <td className={`text-right font-mono ${getReturnColor(u.ann_return)}`}>{pct(u.ann_return)}</td>
                        <td className="text-right font-mono">{pct(u.ann_volatility)}</td>
                        <td className="text-right font-mono">{dec(u.sharpe_ratio)}</td>
                        <td className="text-right font-mono">{dec(u.sortino_ratio)}</td>
                        <td className="text-right font-mono text-red-600">{pct(u.max_drawdown)}</td>
                        <td className="text-right font-mono text-red-600">{pct(u.current_drawdown)}</td>
                        <td className="text-right font-mono">{u.recovery_days != null ? `${u.recovery_days}d` : '-'}</td>
                        <td className="text-right font-mono">{dec(u.skewness)}</td>
                        <td className="text-right font-mono">{dec(u.kurtosis)}</td>
                        <td className="text-right font-mono text-red-600">{pct(u.var_95)}</td>
                        <td className="text-right font-mono text-red-600">{pct(u.cvar_95)}</td>
                        <td className="text-right font-mono">{pct(u.win_rate)}</td>
                        <td className="text-right font-mono text-green-600">{pct(u.best_month)}</td>
                        <td className="text-right font-mono text-red-600">{pct(u.worst_month)}</td>
                        <td className="text-right font-mono text-muted-foreground">{u.num_months || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center text-muted-foreground p-8">No univariate data available</div>
          )}
        </div>
      )}

      {/* ── MODEL WEIGHTS TAB ── */}
      {tab === 'weights' && (
        <div className="space-y-4">
          {weightsLoading ? (
            <div className="flex justify-center p-8"><span className="spinner" /></div>
          ) : weights && weights.length > 0 ? (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="p-4 border-b border-border"><h2 className="font-semibold">Actual vs Model Weights</h2></div>
              <div className="overflow-x-auto">
                <table className="data-table text-xs">
                  <thead>
                    <tr>
                      <th className="sticky left-0 bg-card z-10">Fund</th>
                      <th className="text-right">Current</th>
                      <th className="text-right">ERC</th>
                      <th className="text-right">Inv Vol</th>
                      <th className="text-right">Max Div</th>
                      <th className="text-right">Max Sharpe</th>
                      <th className="text-right">Avg Model</th>
                      <th className="text-right">Diff</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weights.map((w: any) => (
                      <tr key={w.fund_name}>
                        <td className="font-medium sticky left-0 bg-card z-10 whitespace-nowrap">{w.fund_name}</td>
                        <td className="text-right font-mono font-semibold">{pct(w.actual_weight)}</td>
                        <td className="text-right font-mono">{pct(w.erc_weight)}</td>
                        <td className="text-right font-mono">{pct(w.inv_vol_weight)}</td>
                        <td className="text-right font-mono">{pct(w.max_div_weight)}</td>
                        <td className="text-right font-mono">{pct(w.max_sharpe_weight)}</td>
                        <td className="text-right font-mono font-semibold">{pct(w.avg_model_weight)}</td>
                        <td className={`text-right font-mono ${w.difference > 0 ? 'text-green-600' : w.difference < 0 ? 'text-red-600' : ''}`}>
                          {fmtImpact(w.difference)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center text-muted-foreground p-8">No weight data available</div>
          )}
        </div>
      )}

      {/* ── CORRELATIONS TAB ── */}
      {tab === 'correlations' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            <label className="text-sm text-muted-foreground">Type:</label>
            {[
              { key: 'full', label: 'Full Sample' },
              { key: 'rolling', label: `Last ${months}M` },
              { key: 'drawdown', label: 'Drawdown' },
              { key: 'drawdown_implied', label: 'DD Implied' },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setCorrType(t.key)}
                className={`px-3 py-1.5 text-xs rounded-md border ${
                  corrType === t.key
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-input hover:bg-accent'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {corrLoading ? (
            <div className="flex justify-center p-8"><span className="spinner" /></div>
          ) : correlations && correlations.funds?.length > 0 ? (
            <div className="bg-card border border-border rounded-lg overflow-hidden p-4">
              <h2 className="font-semibold mb-4">
                {corrType === 'full' && 'Correlations (Full Sample)'}
                {corrType === 'rolling' && `Correlations (Last ${months} Months)`}
                {corrType === 'drawdown' && 'Drawdown Correlations (COD)'}
                {corrType === 'drawdown_implied' && 'Drawdown-Implied Correlations (DIC)'}
              </h2>
              <HeatmapTable
                data={correlations.matrix}
                rowLabels={correlations.funds}
                colLabels={correlations.funds}
                formatter={(v: number) => v.toFixed(2)}
                colorScale="diverging"
                minValue={-1}
                maxValue={1}
              />
            </div>
          ) : (
            <div className="text-center text-muted-foreground p-8">No correlation data available</div>
          )}
        </div>
      )}

      {/* ── MARGINAL IMPACT TAB ── */}
      {tab === 'marginal' && (
        <div className="space-y-4">
          {marginalLoading ? (
            <div className="flex justify-center p-8"><span className="spinner" /></div>
          ) : marginal && marginal.length > 0 ? (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="p-4 border-b border-border">
                <h2 className="font-semibold">Marginal Diversification Impact</h2>
                <p className="text-xs text-muted-foreground mt-1">
                  Impact on portfolio metrics when each fund is excluded (positive = fund helps)
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="data-table text-xs">
                  <thead>
                    <tr>
                      <th className="sticky left-0 bg-card z-10">Fund</th>
                      <th className="text-right">Perf Impact</th>
                      <th className="text-right">Vol Impact</th>
                      <th className="text-right">Max DD Impact</th>
                      <th className="text-right">Sharpe Impact</th>
                      <th className="text-right">Recovery &Delta;</th>
                      <th className="text-right">Rcvry (excl)</th>
                      <th className="text-center">Period</th>
                    </tr>
                  </thead>
                  <tbody>
                    {marginal.map((m: any) => (
                      <tr key={m.fund_name}>
                        <td className="font-medium sticky left-0 bg-card z-10 whitespace-nowrap">{m.fund_name}</td>
                        <td className={`text-right font-mono ${m.perf_impact > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {fmtImpact(m.perf_impact)}
                        </td>
                        <td className={`text-right font-mono ${m.vol_impact < 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {fmtImpact(m.vol_impact)}
                        </td>
                        <td className={`text-right font-mono ${m.max_dd_impact > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {fmtImpact(m.max_dd_impact)}
                        </td>
                        <td className={`text-right font-mono ${m.sharpe_impact > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {m.sharpe_impact != null ? (m.sharpe_impact > 0 ? '+' : '') + m.sharpe_impact.toFixed(3) : '-'}
                        </td>
                        <td className="text-right font-mono">
                          {m.recovery_days_change != null ? `${m.recovery_days_change > 0 ? '+' : ''}${Math.round(m.recovery_days_change)}d` : '-'}
                        </td>
                        <td className="text-right font-mono text-muted-foreground">
                          {m.recovery_days_excl != null ? `${Math.round(m.recovery_days_excl)}d` : '-'}
                        </td>
                        <td className="text-center text-muted-foreground">{m.start_date} — {m.end_date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center text-muted-foreground p-8">No marginal impact data available (need 3+ funds)</div>
          )}
        </div>
      )}

      {/* ── SKEW-SHARPE TAB ── */}
      {tab === 'skewsharpe' && (
        <div className="space-y-4">
          {ssLoading ? (
            <div className="flex justify-center p-8"><span className="spinner" /></div>
          ) : scatterData.funds.length > 0 ? (
            <div className="bg-card border border-border rounded-lg p-4">
              <h2 className="font-semibold mb-2">Sharpe Ratio vs Skewness</h2>
              <p className="text-xs text-muted-foreground mb-4">Bubble size = Kurtosis. Diamond = Portfolio.</p>
              <ResponsiveContainer width="100%" height={500}>
                <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                  <XAxis
                    type="number"
                    dataKey="skewness"
                    name="Skewness"
                    tick={{ fontSize: 12, fill: 'hsl(215, 16%, 47%)' }}
                    label={{ value: 'Skewness', position: 'bottom', offset: 0, fontSize: 12 }}
                  />
                  <YAxis
                    type="number"
                    dataKey="sharpe_ratio"
                    name="Sharpe"
                    tick={{ fontSize: 12, fill: 'hsl(215, 16%, 47%)' }}
                    label={{ value: 'Sharpe Ratio', angle: -90, position: 'insideLeft', fontSize: 12 }}
                  />
                  <ZAxis type="number" dataKey="kurtosis" range={[80, 600]} name="Kurtosis" />
                  <Tooltip
                    content={({ payload }) => {
                      if (!payload?.length) return null
                      const d = payload[0].payload
                      return (
                        <div className="bg-card border border-border rounded-lg p-2 text-xs shadow-lg">
                          <div className="font-semibold">{d.name}</div>
                          <div>Skewness: {d.skewness?.toFixed(3)}</div>
                          <div>Sharpe: {d.sharpe_ratio?.toFixed(3)}</div>
                          <div>Kurtosis: {d.kurtosis?.toFixed(3)}</div>
                        </div>
                      )
                    }}
                  />
                  <Legend />
                  <ReferenceLine y={0} stroke="hsl(215, 16%, 77%)" strokeDasharray="3 3" />
                  <ReferenceLine x={0} stroke="hsl(215, 16%, 77%)" strokeDasharray="3 3" />
                  <Scatter name="Funds" data={scatterData.funds} fill="hsl(217, 91%, 50%)" opacity={0.7} />
                  <Scatter name="Portfolio" data={scatterData.portfolio} fill="hsl(38, 92%, 50%)" shape="diamond" opacity={0.9} />
                </ScatterChart>
              </ResponsiveContainer>
              {skewSharpe?.regression_slope != null && (
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Regression: y = {skewSharpe.regression_slope.toFixed(3)}x + {skewSharpe.regression_intercept.toFixed(3)}
                </p>
              )}
            </div>
          ) : (
            <div className="text-center text-muted-foreground p-8">No skew-sharpe data available</div>
          )}
        </div>
      )}

      {/* ── UNIQUENESS TAB ── */}
      {tab === 'uniqueness' && (
        <div className="space-y-4">
          {uniqLoading ? (
            <div className="flex justify-center p-8"><span className="spinner" /></div>
          ) : uniqueness?.points?.length > 0 ? (
            <div className="bg-card border border-border rounded-lg p-4">
              <h2 className="font-semibold mb-2">PCA Uniqueness vs Sharpe Ratio</h2>
              <p className="text-xs text-muted-foreground mb-4">
                Bubble size = Volatility. High uniqueness + high Sharpe = best diversifiers.
              </p>
              <ResponsiveContainer width="100%" height={500}>
                <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                  <XAxis
                    type="number" dataKey="uniqueness" name="Uniqueness"
                    tick={{ fontSize: 12, fill: 'hsl(215, 16%, 47%)' }}
                    label={{ value: 'Uniqueness (1 - R²)', position: 'bottom', offset: 0, fontSize: 12 }}
                    domain={[0, 'auto']}
                  />
                  <YAxis
                    type="number" dataKey="sharpe_ratio" name="Sharpe"
                    tick={{ fontSize: 12, fill: 'hsl(215, 16%, 47%)' }}
                    label={{ value: 'Sharpe Ratio', angle: -90, position: 'insideLeft', fontSize: 12 }}
                  />
                  <ZAxis type="number" dataKey="volatility" range={[80, 600]} name="Volatility" />
                  <Tooltip
                    content={({ payload }) => {
                      if (!payload?.length) return null
                      const d = payload[0].payload
                      return (
                        <div className="bg-card border border-border rounded-lg p-2 text-xs shadow-lg">
                          <div className="font-semibold">{d.name}</div>
                          <div>Uniqueness: {(d.uniqueness * 100).toFixed(1)}%</div>
                          <div>Sharpe: {d.sharpe_ratio?.toFixed(3)}</div>
                          <div>Volatility: {(d.volatility * 100).toFixed(2)}%</div>
                          <div className="text-muted-foreground">{d.num_pcs} PCs</div>
                        </div>
                      )
                    }}
                  />
                  <Legend />
                  {uniqueness.median_uniqueness != null && (
                    <ReferenceLine x={uniqueness.median_uniqueness} stroke="hsl(215, 16%, 77%)" strokeDasharray="3 3" label="Median" />
                  )}
                  {uniqueness.median_sharpe != null && (
                    <ReferenceLine y={uniqueness.median_sharpe} stroke="hsl(215, 16%, 77%)" strokeDasharray="3 3" />
                  )}
                  <Scatter name="Funds" data={uniqueness.points} fill="hsl(217, 91%, 50%)" opacity={0.7} />
                </ScatterChart>
              </ResponsiveContainer>
              {/* Quadrant legend */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4 text-xs">
                <div className="p-2 bg-green-500/10 rounded text-center">
                  <div className="font-semibold text-green-700">Stars</div>
                  <div className="text-muted-foreground">High Sharpe + High Uniqueness</div>
                </div>
                <div className="p-2 bg-yellow-500/10 rounded text-center">
                  <div className="font-semibold text-yellow-700">Redundant Alpha</div>
                  <div className="text-muted-foreground">High Sharpe + Low Uniqueness</div>
                </div>
                <div className="p-2 bg-blue-500/10 rounded text-center">
                  <div className="font-semibold text-blue-700">Unique but Weak</div>
                  <div className="text-muted-foreground">Low Sharpe + High Uniqueness</div>
                </div>
                <div className="p-2 bg-red-500/10 rounded text-center">
                  <div className="font-semibold text-red-700">Cut Candidates</div>
                  <div className="text-muted-foreground">Low Sharpe + Low Uniqueness</div>
                </div>
              </div>
              {/* Uniqueness table */}
              <div className="mt-4 overflow-x-auto">
                <table className="data-table text-xs">
                  <thead>
                    <tr>
                      <th>Fund</th>
                      <th className="text-right">Uniqueness</th>
                      <th className="text-right">Sharpe</th>
                      <th className="text-right">Volatility</th>
                      <th className="text-right"># PCs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...uniqueness.points].sort((a: any, b: any) => b.uniqueness - a.uniqueness).map((p: any) => (
                      <tr key={p.name}>
                        <td className="font-medium">{p.name}</td>
                        <td className="text-right font-mono">{(p.uniqueness * 100).toFixed(1)}%</td>
                        <td className="text-right font-mono">{p.sharpe_ratio.toFixed(3)}</td>
                        <td className="text-right font-mono">{(p.volatility * 100).toFixed(2)}%</td>
                        <td className="text-right font-mono text-muted-foreground">{p.num_pcs}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center text-muted-foreground p-8">No uniqueness data available (need 3+ funds)</div>
          )}
        </div>
      )}

      {/* ── CORRELATION QUADRANT TAB ── */}
      {tab === 'corrquadrant' && (
        <div className="space-y-4">
          {cqLoading ? (
            <div className="flex justify-center p-8"><span className="spinner" /></div>
          ) : corrQuadrant?.points?.length > 0 ? (
            <div className="bg-card border border-border rounded-lg p-4">
              <h2 className="font-semibold mb-2">Fund-Portfolio Correlation vs Sharpe Ratio</h2>
              <p className="text-xs text-muted-foreground mb-4">
                Correlation with portfolio (excl. fund) vs Sharpe. Bubble size = |Skewness|. Best funds: high Sharpe, low correlation.
              </p>
              <ResponsiveContainer width="100%" height={500}>
                <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                  <XAxis
                    type="number" dataKey="correlation" name="Correlation"
                    tick={{ fontSize: 12, fill: 'hsl(215, 16%, 47%)' }}
                    label={{ value: 'Correlation with Portfolio (excl)', position: 'bottom', offset: 0, fontSize: 12 }}
                    domain={[-1, 1]}
                  />
                  <YAxis
                    type="number" dataKey="sharpe_ratio" name="Sharpe"
                    tick={{ fontSize: 12, fill: 'hsl(215, 16%, 47%)' }}
                    label={{ value: 'Sharpe Ratio', angle: -90, position: 'insideLeft', fontSize: 12 }}
                  />
                  <ZAxis
                    type="number"
                    dataKey="skewness"
                    range={[80, 600]}
                    name="Skewness"
                  />
                  <Tooltip
                    content={({ payload }) => {
                      if (!payload?.length) return null
                      const d = payload[0].payload
                      return (
                        <div className="bg-card border border-border rounded-lg p-2 text-xs shadow-lg">
                          <div className="font-semibold">{d.name}</div>
                          <div>Correlation: {d.correlation?.toFixed(3)}</div>
                          <div>Sharpe: {d.sharpe_ratio?.toFixed(3)}</div>
                          <div>Skewness: {d.skewness?.toFixed(3)}</div>
                        </div>
                      )
                    }}
                  />
                  <Legend />
                  <ReferenceLine x={0} stroke="hsl(215, 16%, 77%)" strokeDasharray="3 3" />
                  <ReferenceLine y={0} stroke="hsl(215, 16%, 77%)" strokeDasharray="3 3" />
                  {corrQuadrant.portfolio_sharpe != null && (
                    <ReferenceLine y={corrQuadrant.portfolio_sharpe} stroke="hsl(38, 92%, 50%)" strokeDasharray="5 5" label="Portfolio SR" />
                  )}
                  <Scatter name="Funds" data={corrQuadrant.points} fill="hsl(217, 91%, 50%)" opacity={0.7} />
                </ScatterChart>
              </ResponsiveContainer>
              {/* Quadrant legend */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4 text-xs">
                <div className="p-2 bg-green-500/10 rounded text-center">
                  <div className="font-semibold text-green-700">Best Diversifiers</div>
                  <div className="text-muted-foreground">High Sharpe + Low Correlation</div>
                </div>
                <div className="p-2 bg-yellow-500/10 rounded text-center">
                  <div className="font-semibold text-yellow-700">Redundant Alpha</div>
                  <div className="text-muted-foreground">High Sharpe + High Correlation</div>
                </div>
                <div className="p-2 bg-blue-500/10 rounded text-center">
                  <div className="font-semibold text-blue-700">Diversifying but Weak</div>
                  <div className="text-muted-foreground">Low Sharpe + Low Correlation</div>
                </div>
                <div className="p-2 bg-red-500/10 rounded text-center">
                  <div className="font-semibold text-red-700">Cut Candidates</div>
                  <div className="text-muted-foreground">Low Sharpe + High Correlation</div>
                </div>
              </div>
              {/* Table */}
              <div className="mt-4 overflow-x-auto">
                <table className="data-table text-xs">
                  <thead>
                    <tr>
                      <th>Fund</th>
                      <th className="text-right">Correlation</th>
                      <th className="text-right">Sharpe</th>
                      <th className="text-right">Skewness</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...corrQuadrant.points].sort((a: any, b: any) => a.correlation - b.correlation).map((p: any) => (
                      <tr key={p.name}>
                        <td className="font-medium">{p.name}</td>
                        <td className="text-right font-mono">{p.correlation.toFixed(3)}</td>
                        <td className="text-right font-mono">{p.sharpe_ratio.toFixed(3)}</td>
                        <td className="text-right font-mono">{p.skewness.toFixed(3)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center text-muted-foreground p-8">No correlation quadrant data available (need 3+ funds)</div>
          )}
        </div>
      )}

      {/* ── CONDITIONAL TAB ── */}
      {tab === 'conditional' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground">Benchmark:</label>
              {condBenchmarks.map((b: string) => (
                <button
                  key={b}
                  onClick={() => setCondBench(b)}
                  className={`px-3 py-1.5 text-xs rounded-md border ${
                    condBench === b
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-input hover:bg-accent'
                  }`}
                >
                  {b}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground">Metric:</label>
              {condMetrics.map((m: string) => (
                <button
                  key={m}
                  onClick={() => setCondMetric(m)}
                  className={`px-3 py-1.5 text-xs rounded-md border capitalize ${
                    condMetric === m
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-input hover:bg-accent'
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          {condLoading ? (
            <div className="flex justify-center p-8"><span className="spinner" /></div>
          ) : filteredCond.length > 0 ? (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="p-4 border-b border-border">
                <h2 className="font-semibold">Conditional {condMetric} — {condBench}</h2>
                <p className="text-xs text-muted-foreground mt-1">
                  Fund {condMetric} conditioned on {condBench} market regimes
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="data-table text-xs">
                  <thead>
                    <tr>
                      <th className="sticky left-0 bg-card z-10">Fund</th>
                      <th className="text-right">Unconditional</th>
                      <th className="text-right">Lower Q</th>
                      <th className="text-right">Negative</th>
                      <th className="text-right">Positive</th>
                      <th className="text-right">Upper Q</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCond.map((r: any, i: number) => {
                      const fmt = condMetric === 'performance' ? pct : (v: number | null | undefined) => dec(v, 3)
                      return (
                        <tr key={i}>
                          <td className="font-medium sticky left-0 bg-card z-10 whitespace-nowrap">{r.fund}</td>
                          <td className="text-right font-mono">{fmt(r.unconditional)}</td>
                          <td className={`text-right font-mono ${r.conditional_lower_quartile < 0 ? 'text-red-600' : ''}`}>
                            {fmt(r.conditional_lower_quartile)}
                          </td>
                          <td className={`text-right font-mono ${r.conditional_negative < 0 ? 'text-red-600' : ''}`}>
                            {fmt(r.conditional_negative)}
                          </td>
                          <td className={`text-right font-mono ${r.conditional_positive > 0 ? 'text-green-600' : ''}`}>
                            {fmt(r.conditional_positive)}
                          </td>
                          <td className={`text-right font-mono ${r.conditional_upper_quartile > 0 ? 'text-green-600' : ''}`}>
                            {fmt(r.conditional_upper_quartile)}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center text-muted-foreground p-8">
              {conditional?.length ? 'No data for selected filters' : 'No conditional data available'}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
